'use client';

/**
 * SectionErrorBoundary — lightweight error boundary for landing sections.
 *
 * Catches runtime errors in child components (e.g. TestimonialsSection,
 * PricingSection) and renders a graceful fallback instead of crashing
 * the entire page. The rest of the page continues to render normally.
 *
 * Usage:
 *   <SectionErrorBoundary fallback={null}>
 *     <TestimonialsSection />
 *   </SectionErrorBoundary>
 *
 * Spec ref: SPEC-136 §5 (graceful degradation — landing section shows
 * error message instead of crashing the whole page).
 *
 * §6 Anti-Invención: pure error handling — no business logic.
 */

import { Component, type ReactNode } from 'react';

interface SectionErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface SectionErrorBoundaryState {
  hasError: boolean;
}

export class SectionErrorBoundary extends Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): SectionErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, errorInfo: { componentStack: string }): void {
    // Log to console for debugging (not PII — just error stack).
    // Using console.warn (not console.error) to avoid Sentry noise for
    // non-critical landing section failures.
    console.warn('SectionErrorBoundary caught error:', error.message, errorInfo.componentStack);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}
